import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const CreateAuthorizationForm = ({ onCreated }) => {
  const { tenant_id, user } = useAuth();
  const user_id = user?.user_id || null;

  const [formData, setFormData] = useState({
    consumer_id: "",
    counselor_id: "",
    plan_id: "",
    service_id: "",
    authorization_number: "",
    approved_by: "",
    approval_date: "",
    authorization_hours: "",
    authorization_rate_variance: "",
    auth_type: "service",
  });

  const [message, setMessage] = useState("");
  const [consumers, setConsumers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [authWithoutPlan, setAuthWithoutPlan] = useState(false);
  const [allServices, setAllServices] = useState([]);

  // Milestone-related state
  const [milestoneTemplates, setMilestoneTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [milestoneMode, setMilestoneMode] = useState("none"); // "none", "template", "auto", "custom"
  const [customMilestoneCount, setCustomMilestoneCount] = useState(1);
  const [customMilestones, setCustomMilestones] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [showPrivacyNotice, setShowPrivacyNotice] = useState(true);

  // Find the selected CRP-20 plan
  const selectedPlan = plans.find((p) => p.plan_id === formData.plan_id);

  const availableServices = authWithoutPlan
    ? allServices
    : selectedPlan?.services || [];

  // Check if selected service is flat rate
  const selectedService = availableServices.find(
    (s) => s.service_id === formData.service_id
  );
  const isFlatRateService = selectedService?.flat_rate === true;

  // Load consumers for the tenant
  useEffect(() => {
    if (!tenant_id || !user_id || !user?.role || !user?.tenant_type) return;

    const fetchConsumers = async () => {
      try {
        const res = await secureAxios.get("/api/consumers", {
          params: {
            tenant_id,
            user_id,
            role: user.role,
            tenant_type: user.tenant_type,
          },
        });
        setConsumers(res.data);
      } catch (err) {
        console.error("Failed to load consumers", err);
        toast.error("Failed to load consumers");
      }
    };

    fetchConsumers();
  }, [tenant_id, user_id, user]);

  // When consumer changes, auto-fill counselor and load matching plans
  useEffect(() => {
    const { consumer_id } = formData;
    if (!consumer_id) return;

    const selectedConsumer = consumers.find(
      (c) => c.consumer_id === consumer_id
    );
    if (selectedConsumer?.counselor_id) {
      setFormData((prev) => ({
        ...prev,
        counselor_id: selectedConsumer.counselor_id,
      }));
    }

    // Always clear dependent fields first
    setFormData((prev) => ({
      ...prev,
      plan_id: "",
      service_id: "",
    }));

    // Reset milestone state
    resetMilestoneState();

    // Load plans or services based on authWithoutPlan flag
    const loadPlansOrServices = async () => {
      try {
        if (authWithoutPlan && selectedConsumer?.counselor_id) {
          // Load services directly for auth without plan
          const counselorRes = await secureAxios.get(
            `/api/counselors/${selectedConsumer.counselor_id}`,
            { params: { tenant_id } }
          );
          const customer_id = counselorRes.data.customer_id;

          if (customer_id) {
            const servicesRes = await secureAxios.get("/api/services", {
              params: { tenant_id, customer_id },
            });
            setAllServices(servicesRes.data);
          }
        } else {
          // Load CRP-20 Plans only (CRP-30 no longer used for authorizations)
          const plansRes = await secureAxios.get(
            `/api/plans/${consumer_id}/with-available-services`,
            { params: { tenant_id } }
          );
          setPlans(plansRes.data);
        }
      } catch (err) {
        console.error("Failed to load data", err);
        if (authWithoutPlan) {
          toast.error("Failed to load services");
        } else {
          setPlans([]);
        }
      }
    };

    loadPlansOrServices();
  }, [formData.consumer_id, consumers, tenant_id, authWithoutPlan]);

  // Load milestone templates when flat rate service is selected
  useEffect(() => {
    if (!isFlatRateService || !tenant_id) {
      setMilestoneTemplates([]);
      return;
    }

    setLoadingTemplates(true);

    // Get customer_id for filtering templates
    const selectedConsumer = consumers.find(
      (c) => c.consumer_id === formData.consumer_id
    );

    secureAxios
      .get("/api/milestone-templates", {
        params: {
          tenant_id,
          include_items: "true",
          // Optionally filter by customer
        },
      })
      .then((res) => {
        setMilestoneTemplates(res.data);
        setLoadingTemplates(false);
      })
      .catch((err) => {
        console.error("Error loading milestone templates:", err);
        setLoadingTemplates(false);
      });
  }, [isFlatRateService, tenant_id, formData.consumer_id, consumers]);

  // Reset milestone state
  const resetMilestoneState = () => {
    setMilestoneMode("none");
    setSelectedTemplateId("");
    setCustomMilestoneCount(1);
    setCustomMilestones([]);
  };

  // Handle service change - reset milestones when service changes
  const handleServiceChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, service_id: value }));
    resetMilestoneState();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle plan selection (CRP-20 plans only)
  const handlePlanChange = (e) => {
    const selectedValue = e.target.value;

    setFormData((prev) => ({
      ...prev,
      plan_id: selectedValue,
      service_id: "",
    }));

    resetMilestoneState();
  };

  // Handle milestone mode change
  const handleMilestoneModeChange = (mode) => {
    setMilestoneMode(mode);
    setSelectedTemplateId("");
    setCustomMilestones([]);

    if (mode === "auto") {
      // Auto-generate based on authorization_hours
      const units = parseInt(formData.authorization_hours) || 1;
      setCustomMilestoneCount(units);
    } else if (mode === "custom") {
      setCustomMilestones([{ milestone_name: "Unit 1", description: "" }]);
    }
  };

  // Handle template selection - populate editable milestones
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    
    if (!templateId) {
      setCustomMilestones([]);
      return;
    }

    // Find the template and populate editable milestones
    const template = milestoneTemplates.find((t) => t.template_id === templateId);
    if (template?.items && template.items.length > 0) {
      const editableMilestones = template.items
        .sort((a, b) => a.sequence_order - b.sequence_order)
        .map((item) => ({
          milestone_name: item.milestone_name,
          description: item.description || "",
        }));
      setCustomMilestones(editableMilestones);
    } else {
      setCustomMilestones([]);
    }
  };

  // Handle custom milestone changes
  const updateCustomMilestone = (index, field, value) => {
    const updated = [...customMilestones];
    updated[index] = { ...updated[index], [field]: value };
    setCustomMilestones(updated);
  };

  const addCustomMilestone = () => {
    setCustomMilestones([
      ...customMilestones,
      {
        milestone_name: `Unit ${customMilestones.length + 1}`,
        description: "",
      },
    ]);
  };

  const removeCustomMilestone = (index) => {
    if (customMilestones.length <= 1) return;
    setCustomMilestones(customMilestones.filter((_, i) => i !== index));
  };

  // Create milestones for the authorization
  const createMilestonesForAuth = async (authorizationId) => {
    if (milestoneMode === "none") return;

    try {
      let payload = {};

      if (milestoneMode === "template" && customMilestones.length > 0) {
        // Use edited template milestones (stored in customMilestones)
        payload = {
          milestones: customMilestones.map((m, index) => ({
            milestone_name: m.milestone_name || `Unit ${index + 1}`,
            description: m.description || "",
            sequence_order: index + 1,
          })),
          // Optionally include template_id for reference
          template_id: selectedTemplateId || null,
        };
      } else if (milestoneMode === "auto") {
        // Auto-generate based on count
        const count = parseInt(formData.authorization_hours) || 1;
        const milestones = [];
        for (let i = 1; i <= count; i++) {
          milestones.push({
            milestone_name: `Unit ${i}`,
            description: "",
            sequence_order: i,
          });
        }
        payload = { milestones };
      } else if (milestoneMode === "custom" && customMilestones.length > 0) {
        // Custom milestones
        payload = {
          milestones: customMilestones.map((m, index) => ({
            milestone_name: m.milestone_name || `Unit ${index + 1}`,
            description: m.description || "",
            sequence_order: index + 1,
          })),
        };
      }

      await secureAxios.post(
        `/api/auth-milestones/auth/${authorizationId}`,
        payload
      );

      console.log("Milestones created successfully for auth:", authorizationId);
    } catch (err) {
      console.error("Error creating milestones:", err);
      // Don't fail the whole operation, just warn
      toast.warning(
        "Authorization created, but milestones could not be added. You can add them manually."
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate milestone selection for flat rate with multiple units
    if (isFlatRateService && parseFloat(formData.authorization_hours) > 1) {
      if (milestoneMode === "none") {
        // Warn user but allow them to proceed
        const proceed = window.confirm(
          "This is a flat rate service with multiple units. Without milestones, it will be billed as a single charge.\n\nDo you want to continue without milestones?"
        );
        if (!proceed) return;
      }
    }

    // HARD STOP: Block saving when milestone count doesn't match unit count
    if (
      isFlatRateService &&
      milestoneMode !== "none" &&
      milestoneMode !== "auto" &&
      customMilestones.length > 0 &&
      parseInt(formData.authorization_hours) > 0 &&
      customMilestones.length !== parseInt(formData.authorization_hours)
    ) {
      toast.error(
        `Cannot save: You have ${customMilestones.length} milestone(s) but ${parseInt(formData.authorization_hours)} unit(s). Please adjust the milestones or the number of units to match.`
      );
      return;
    }

    try {
      // Log the attempt to create authorization
      await auditApi.logEntry({
        action: "CREATE_AUTHORIZATION_ATTEMPT",
        entity_type: "AUTHORIZATION",
        entity_identifier: `Auth for ${
          consumers.find((c) => c.consumer_id === formData.consumer_id)?.name ||
          "Unknown"
        }`,
        purpose_of_use: "OPERATIONS",
        contains_phi: false,
      });

      // Build the payload
      const payload = {
        consumer_id: formData.consumer_id,
        counselor_id: formData.counselor_id,
        plan_id: formData.plan_id || null,
        service_id: formData.service_id,
        authorization_number: formData.authorization_number,
        approved_by: formData.approved_by,
        approval_date: formData.approval_date,
        authorization_hours: formData.authorization_hours,
        authorization_rate_variance: formData.authorization_rate_variance,
        auth_type: formData.auth_type,
        user_id,
        created_by: user_id,
      };

      const response = await secureAxios.post("/api/auths", payload);
      const newAuthId = response.data?.authorization_id || response.data?.id;

      // Create milestones if applicable
      if (newAuthId && isFlatRateService && milestoneMode !== "none") {
        // For template and custom modes, we need customMilestones
        // For auto mode, we generate them in createMilestonesForAuth
        if (milestoneMode === "auto" || customMilestones.length > 0) {
          await createMilestonesForAuth(newAuthId);
        }
      }

      // Reset form after creation
      setFormData({
        consumer_id: "",
        counselor_id: "",
        plan_id: "",
        service_id: "",
        authorization_number: "",
        approved_by: "",
        approval_date: "",
        authorization_hours: "",
        authorization_rate_variance: "",
        auth_type: "service",
      });
      setPlans([]);
      setAllServices([]);
      setAuthWithoutPlan(false);
      resetMilestoneState();

      setMessage("Authorization created successfully.");
      toast.success("Authorization created successfully");

      // Log successful creation
      await auditApi.logEntry({
        action: "CREATE_AUTHORIZATION_SUCCESS",
        entity_type: "AUTHORIZATION",
        entity_identifier: `Auth for ${
          consumers.find((c) => c.consumer_id === formData.consumer_id)?.name ||
          "Unknown"
        }`,
        purpose_of_use: "OPERATIONS",
        contains_phi: true,
        phi_types: ["authorization_info"],
      });

      if (onCreated) onCreated();

      // Clear the message after 10 seconds
      setTimeout(() => {
        setMessage("");
      }, 10000);
    } catch (err) {
      console.error("Authorization creation failed:", err);
      const errorMsg =
        err.response?.data?.error || "Failed to create authorization";
      setMessage(`Error: ${errorMsg}`);
      toast.error(errorMsg);

      // Log the failure
      await auditApi.logEntry({
        action: "CREATE_AUTHORIZATION_ATTEMPT",
        entity_type: "AUTHORIZATION",
        action_status: "FAILED",
        action_reason: err.message,
        purpose_of_use: "OPERATIONS",
        contains_phi: false,
      });
    }
  };

  return (
    <div>
      {/* HIPAA Compliance Statement */}
      {showPrivacyNotice && (
        <div className="alert alert-warning alert-dismissible mb-4">
          <button 
            type="button" 
            className="btn-close" 
            aria-label="Close"
            onClick={() => setShowPrivacyNotice(false)}
          ></button>
          <h6 className="alert-heading">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Privacy & Compliance Notice
          </h6>
          <p className="mb-2">
            By creating this authorization, you acknowledge that:
          </p>
          <ul className="mb-0">
            <li>
              This authorization may contain Protected Health Information (PHI)
            </li>
            <li>You are authorized to access and manage this information</li>
            <li>All actions are logged for HIPAA compliance</li>
            <li>Information should only be shared on a need-to-know basis</li>
            <li>
              You must safeguard any PHI in accordance with HIPAA regulations
            </li>
          </ul>
        </div>
      )}

      {message && (
        <div
          className={`alert ${
            message.startsWith("Error") ? "alert-danger" : "alert-success"
          }`}
          role="alert"
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-md-4">
          <label className="form-label">Consumer</label>
          <select
            className="form-select"
            name="consumer_id"
            value={formData.consumer_id}
            onChange={handleChange}
            required
          >
            <option value="">Select Consumer</option>
            {consumers.map((c) => (
              <option key={c.consumer_id} value={c.consumer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">Counselor</label>
          <input
            type="text"
            className="form-control"
            value={
              consumers.find((c) => c.consumer_id === formData.consumer_id)
                ?.counselor_name || ""
            }
            disabled
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Authorization Type</label>
          <select
            className="form-select"
            name="auth_type"
            value={formData.auth_type}
            onChange={handleChange}
          >
            <option value="service">Service</option>
            <option value="mileage">Mileage</option>
          </select>
        </div>

        <div className="col-md-12 form-check ms-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="authWithoutPlan"
            checked={authWithoutPlan}
            onChange={(e) => {
              setAuthWithoutPlan(e.target.checked);
              setFormData((prev) => ({
                ...prev,
                plan_id: "",
                job_placement_id: "",
                plan_type: "",
                service_id: "",
              }));
              setAllServices([]);
              resetMilestoneState();
            }}
          />
          <label className="form-check-label" htmlFor="authWithoutPlan">
            Create Authorization Without a Plan
          </label>
        </div>

        {!authWithoutPlan && (
          <div className="col-md-4">
            <label className="form-label">Plan</label>
            <select
              className="form-select"
              name="plan_id"
              value={formData.plan_id}
              onChange={handlePlanChange}
              required={!authWithoutPlan}
            >
              <option value="">Select Plan</option>

              {/* CRP-20 Service Plans */}
              {plans.filter((p) => p.services?.length > 0).length > 0 && (
                <optgroup label="CRP-20 Service Plans">
                  {plans
                    .filter((p) => p.services?.length > 0)
                    .map((p) => (
                      <option key={p.plan_id} value={p.plan_id}>
                        {p.plan_name} (
                        {p.services.map((s) => s.service_name).join(", ")})
                      </option>
                    ))}
                </optgroup>
              )}
            </select>

            {/* Helper text showing plan counts */}
            {formData.consumer_id && !authWithoutPlan && (
              <small className="text-muted">
                {plans.filter((p) => p.services?.length > 0).length} CRP-20
                plan(s) available
              </small>
            )}
          </div>
        )}

        <div className="col-md-4">
          <label className="form-label">Service</label>

          {availableServices.length > 1 && authWithoutPlan && (
            <div className="text-warning mb-1">
              Multiple services found - select only one for this authorization.
            </div>
          )}

          <select
            className="form-select"
            name="service_id"
            value={formData.service_id}
            onChange={handleServiceChange}
            required
          >
            <option value="">Select Service</option>
            {availableServices.map((s) => (
              <option key={s.service_id} value={s.service_id}>
                {s.service_name}
                {s.flat_rate && " (Flat Rate)"}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">Authorization Number</label>
          <input
            className="form-control"
            name="authorization_number"
            value={formData.authorization_number}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Approval Date</label>
          <input
            type="date"
            className="form-control"
            name="approval_date"
            value={formData.approval_date}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Approved By</label>
          <input
            type="text"
            className="form-control"
            name="approved_by"
            value={formData.approved_by}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">
            {isFlatRateService ? "Number of Units" : "Authorization Hours/Miles"}
          </label>
          <input
            type="number"
            step={isFlatRateService ? "1" : "0.01"}
            min={isFlatRateService ? "1" : "0"}
            className="form-control"
            name="authorization_hours"
            value={formData.authorization_hours}
            onChange={(e) => {
              handleChange(e);
              // Update auto milestone count if in auto mode
              if (milestoneMode === "auto") {
                setCustomMilestoneCount(parseInt(e.target.value) || 1);
              }
            }}
          />
          {isFlatRateService && (
            <small className="text-muted">
              Each unit will be billed at the flat rate (${selectedService?.rate || 0})
            </small>
          )}
        </div>

        {/* Milestone Configuration - Only show for flat rate services */}
        {isFlatRateService && formData.service_id && (
          <div className="col-12">
            <div className="card border-info">
              <div className="card-header bg-info text-white">
                <i className="bi bi-list-check me-2"></i>
                Milestone Configuration (Flat Rate Billing)
              </div>
              <div className="card-body">
                <p className="text-muted small mb-3">
                  Milestones allow you to track and bill each unit separately. 
                  Choose how you want to set up billing milestones for this authorization.
                </p>

                {/* Milestone Mode Selection */}
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="milestoneMode"
                      id="milestoneNone"
                      checked={milestoneMode === "none"}
                      onChange={() => handleMilestoneModeChange("none")}
                    />
                    <label className="form-check-label" htmlFor="milestoneNone">
                      <strong>No Milestones</strong> - Bill as a single charge
                      <small className="text-muted d-block">
                        Legacy behavior: entire authorization billed at once
                      </small>
                    </label>
                  </div>

                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="milestoneMode"
                      id="milestoneAuto"
                      checked={milestoneMode === "auto"}
                      onChange={() => handleMilestoneModeChange("auto")}
                    />
                    <label className="form-check-label" htmlFor="milestoneAuto">
                      <strong>Auto-Generate</strong> - Create Unit 1, Unit 2, etc.
                      <small className="text-muted d-block">
                        Automatically creates {parseInt(formData.authorization_hours) || 1} milestone(s) 
                        based on unit count
                      </small>
                    </label>
                  </div>

                  {milestoneTemplates.length > 0 && (
                    <div className="form-check mt-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="milestoneMode"
                        id="milestoneTemplate"
                        checked={milestoneMode === "template"}
                        onChange={() => handleMilestoneModeChange("template")}
                      />
                      <label className="form-check-label" htmlFor="milestoneTemplate">
                        <strong>Use Template</strong> - Apply a predefined milestone template
                        <small className="text-muted d-block">
                          {milestoneTemplates.length} template(s) available
                        </small>
                      </label>
                    </div>
                  )}

                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="milestoneMode"
                      id="milestoneCustom"
                      checked={milestoneMode === "custom"}
                      onChange={() => handleMilestoneModeChange("custom")}
                    />
                    <label className="form-check-label" htmlFor="milestoneCustom">
                      <strong>Custom</strong> - Define your own milestones
                      <small className="text-muted d-block">
                        Create milestones with custom names and descriptions
                      </small>
                    </label>
                  </div>
                </div>

                {/* Template Selection */}
                {milestoneMode === "template" && (
                  <div className="mb-3">
                    <label className="form-label">Select Template</label>
                    <select
                      className="form-select"
                      value={selectedTemplateId}
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                      disabled={loadingTemplates}
                    >
                      <option value="">-- Select a Template --</option>
                      {milestoneTemplates.map((t) => (
                        <option key={t.template_id} value={t.template_id}>
                          {t.template_name} ({t.items?.length || t.item_count || 0} milestones)
                        </option>
                      ))}
                    </select>

                    {/* Error: Milestone count mismatch - must fix before saving */}
                    {selectedTemplateId && customMilestones.length > 0 && 
                     parseInt(formData.authorization_hours) > 0 &&
                     customMilestones.length !== parseInt(formData.authorization_hours) && (
                      <div className="alert alert-danger py-2 mt-2 mb-0">
                        <i className="bi bi-x-circle me-2"></i>
                        <strong>Cannot save:</strong> You have {customMilestones.length} milestone(s) 
                        but {parseInt(formData.authorization_hours)} unit(s). 
                        Please add/remove milestones or change the number of units to match.
                      </div>
                    )}

                    {/* Editable Template Milestones */}
                    {selectedTemplateId && customMilestones.length > 0 && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label mb-0">
                            <i className="bi bi-pencil me-1"></i>
                            Customize Milestones
                          </label>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={addCustomMilestone}
                          >
                            + Add Milestone
                          </button>
                        </div>
                        <small className="text-muted d-block mb-2">
                          Edit milestone names and descriptions as needed, or add/remove milestones.
                        </small>

                        {customMilestones.map((milestone, index) => (
                          <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                            <span className="badge bg-secondary">{index + 1}</span>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Milestone name"
                              value={milestone.milestone_name}
                              onChange={(e) =>
                                updateCustomMilestone(index, "milestone_name", e.target.value)
                              }
                              style={{ flex: 2 }}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Description (optional)"
                              value={milestone.description}
                              onChange={(e) =>
                                updateCustomMilestone(index, "description", e.target.value)
                              }
                              style={{ flex: 2 }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeCustomMilestone(index)}
                              disabled={customMilestones.length <= 1}
                              title="Remove milestone"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Auto-Generate Preview */}
                {milestoneMode === "auto" && (
                  <div className="p-2 bg-light rounded">
                    <strong className="small">Will create:</strong>
                    <ul className="mb-0 small">
                      {Array.from(
                        { length: parseInt(formData.authorization_hours) || 1 },
                        (_, i) => (
                          <li key={i}>Unit {i + 1}</li>
                        )
                      )}
                    </ul>
                  </div>
                )}

                {/* Custom Milestones */}
                {milestoneMode === "custom" && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label mb-0">Custom Milestones</label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={addCustomMilestone}
                      >
                        + Add Milestone
                      </button>
                    </div>

                    {/* Error: Milestone count mismatch - must fix before saving */}
                    {customMilestones.length > 0 && 
                     parseInt(formData.authorization_hours) > 0 &&
                     customMilestones.length !== parseInt(formData.authorization_hours) && (
                      <div className="alert alert-danger py-2 mb-2">
                        <i className="bi bi-x-circle me-2"></i>
                        <strong>Cannot save:</strong> You have {customMilestones.length} milestone(s) 
                        but {parseInt(formData.authorization_hours)} unit(s). 
                        Please add/remove milestones or change the number of units to match.
                      </div>
                    )}

                    {customMilestones.map((milestone, index) => (
                      <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                        <span className="badge bg-secondary">{index + 1}</span>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Milestone name"
                          value={milestone.milestone_name}
                          onChange={(e) =>
                            updateCustomMilestone(index, "milestone_name", e.target.value)
                          }
                          style={{ flex: 2 }}
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Description (optional)"
                          value={milestone.description}
                          onChange={(e) =>
                            updateCustomMilestone(index, "description", e.target.value)
                          }
                          style={{ flex: 2 }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeCustomMilestone(index)}
                          disabled={customMilestones.length <= 1}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="col-md-12">
          <button 
            className="btn btn-success" 
            type="submit"
            disabled={
              isFlatRateService &&
              milestoneMode !== "none" &&
              milestoneMode !== "auto" &&
              customMilestones.length > 0 &&
              parseInt(formData.authorization_hours) > 0 &&
              customMilestones.length !== parseInt(formData.authorization_hours)
            }
          >
            <i className="bi bi-check-circle me-1"></i>
            Create Authorization
            {isFlatRateService && milestoneMode !== "none" && " with Milestones"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAuthorizationForm;